import React, { useEffect, useRef, useState } from 'react';

import { Button } from '../Button';
import { Input } from '../Input';
import { Radio } from '../Radio';
import { Select } from '../Select';
import { Text } from '../Text';
import { Textarea } from '../Textarea';
import { cx } from 'utility';
import * as styles from './ContactForm.module.scss';

function getClasses({ className }) {
    const classes = cx(
        className,
        styles['contactForm'],
    );

    return classes || undefined;
}

function ContactForm({
    className,
    defaultService,
    onSurface,
}) {
    const marketingRadioRef = useRef(null);
    const ecommerceRadioRef = useRef(null);
    const [state, setState] = useState(() => {
        return {
            values: {
                project: (defaultService),
                cms: '',
                framework: '',
                deployment: '',
                ecommerce: '',
                service: '',
                budget: '',
                deadline: '',
                name: '',
                email: '',
                website: '',
                message: '',
            },
        }
    });

    const classes = getClasses({ className });
    const renderEcommerceField = (state.values.project === 'Jamstack eCommerce site') ? true : false;
    let disableSubmitButton = true;

    useEffect(() => {
        if (state.values.project === 'Jamstack marketing site') {
            marketingRadioRef.current.checked = true;
        }
        else if (state.values.project === 'Jamstack eCommerce site') {
            ecommerceRadioRef.current.checked = true;
        }
    }, [state.values.project]);

    useEffect(() => {
        if (state.values.project !== 'Jamstack eCommerce site') {
            setState((previousState) => {
                return {
                    ...previousState,
                    values: {
                        ...previousState.values,
                        ['ecommerce']: '',
                    },
                }
            });
        }
    }, [state.values.project]);

    if (state.values.project === 'Jamstack eCommerce site' && state.values.project && state.values.cms && state.values.framework && state.values.deployment && state.values.ecommerce && state.values.service && state.values.budget && state.values.deadline && state.values.name && state.values.email) {
        disableSubmitButton = false;
    }
    else if (state.values.project === 'Jamstack marketing site' && state.values.project && state.values.cms && state.values.framework && state.values.deployment && state.values.service && state.values.budget && state.values.deadline && state.values.name && state.values.email) {
        disableSubmitButton = false;
    }

    function handleFieldChange(event) {
        event.persist();

        setState((previousState) => {
            return {
                ...previousState,
                values: {
                    ...previousState.values,
                    [event.target.name]: event.target.value,
                },
            }
        });
    }

    function handleSelectFieldChange(name, value) {
        setState((previousState) => {
            return {
                ...previousState,
                values: {
                    ...previousState.values,
                    [name]: value,
                },
            }
        });
    }

    return (
        <form action="/contact-success" className={classes} id="contact" method="POST" name="contact" data-netlify="true" netlify-honeypot="bot-field">
            <fieldset className={styles['radioGroup']}>
                <Text as="legend" className={styles['radioGroupLegend']} emphasis="medium" size={2}>What type of project do you need completed?</Text>
                <div className={styles['radioGroupInlineOpts']}>
                    <Radio
                        id="contact-marketing-site"
                        label="Jamstack marketing site"
                        name="project"
                        onChange={handleFieldChange}
                        onSurface={onSurface}
                        ref={marketingRadioRef}
                        required={true}
                    />
                    <Radio
                        id="contact-ecommerce-site"
                        label="Jamstack eCommerce site"
                        name="project"
                        onChange={handleFieldChange}
                        onSurface={onSurface}
                        ref={ecommerceRadioRef}
                        required={true}
                    />
                </div>
            </fieldset>
            <Select
                id="contact-cms"
                items={["Prismic", "Dato", "Other", "Not sure"]}
                label="What headless CMS do you want to use?"
                name="cms"
                onChange={handleSelectFieldChange}
                onSurface={onSurface}
                required={true}
            />
            <Select
                id="contact-framework"
                items={["Gatsby", "Next", "Other", "Not sure"]}
                label="What framework do you want to use?"
                name="framework"
                onChange={handleSelectFieldChange}
                onSurface={onSurface}
                required={true}
            />
            <Select
                id="contact-deployment"
                items={["Netlify", "Vercel", "Other", "Not sure"]}
                label="Which deployment platform do you want to use?"
                name="deployment"
                onChange={handleSelectFieldChange}
                onSurface={onSurface}
                required={true}
            />
            {renderEcommerceField && (
                <Select
                    id="contact-ecommerce"
                    items={["Shopify", "Other", "Not sure"]}
                    label="Which eCommerce platform do you want to use?"
                    name="ecommerce"
                    onChange={handleSelectFieldChange}
                    onSurface={onSurface}
                    required={true}
                />
            )}
            <fieldset className={styles['radioGroup']}>
                <Text as="legend" className={styles['radioGroupLegend']} emphasis="medium" size={2}>Which services do you need?</Text>
                <div className={styles['radioGroupInlineOpts']}>
                    <Radio
                        id="contact-development"
                        label="Development only"
                        name="service"
                        onChange={handleFieldChange}
                        onSurface={onSurface}
                        required={true}
                    />
                    <Radio
                        id="contact-development-design"
                        label="Design & development"
                        name="service"
                        onChange={handleFieldChange}
                        onSurface={onSurface}
                        required={true}
                    />
                </div>
            </fieldset>
            <Select
                description="Consider this a starting point to our conversation, not a final quote. This will help me guide you toward the most appropriate solution within your budget."
                id="contact-budget"
                items={["Less than £7,500", "£7,500 - £15,000", "£15,000 - £20,000", "£20,000+"]}
                label="What’s the budget for your project?"
                name="budget"
                onChange={handleSelectFieldChange}
                onSurface={onSurface}
                required={true}
            />
            <Select
                id="contact-deadline"
                items={["1-2 months", "2-3 months", "3-6 months", "6 months +"]}
                label="When do you need this project completed?"
                name="deadline"
                onChange={handleSelectFieldChange}
                onSurface={onSurface}
                required={true}
            />
            <Input
                id="contact-full-name"
                label="Full name"
                name="name"
                onChange={handleFieldChange}
                onSurface={onSurface}
                required={true}
            />
            <Input
                id="contact-email"
                label="Email"
                name="email"
                onChange={handleFieldChange}
                onSurface={onSurface}
                required={true}
                type="email"
            />
            <Input
                id="contact-website"
                label="Website"
                name="website"
                onChange={handleFieldChange}
                onSurface={onSurface}
                required={false}
                type="url"
            />
            <Textarea
                id="contact-message"
                label="Any other details"
                name="message"
                onChange={handleFieldChange}
                onSurface={onSurface}
                required={false}
            />
            <Button className={styles['submitButton']} color="secondary" disabled={disableSubmitButton} name="submit" type="submit">Send project details</Button>

            <input type="hidden" name="project" value={state.values.project} />
            <input type="hidden" name="cms" value={state.values.cms} />
            <input type="hidden" name="framework" value={state.values.framework} />
            <input type="hidden" name="deployment" value={state.values.deployment} />
            <input type="hidden" name="ecommerce" value={state.values.ecommerce} />
            <input type="hidden" name="service" value={state.values.service} />
            <input type="hidden" name="budget" value={state.values.budget} />
            <input type="hidden" name="deadline" value={state.values.deadline} />
            <input type="hidden" name="name" value={state.values.name} />
            <input type="hidden" name="email" value={state.values.email} />
            <input type="hidden" name="website" value={state.values.website} />
            <input type="hidden" name="message" value={state.values.message} />
            <input type="hidden" name="bot-field" />
            <input type="hidden" name="form-name" value="contact" />
        </form>
    );
}

export { ContactForm };
